import { Loader2, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import type { NeighborEdge, NeighborNode } from '@/components/neo4j/shared/entity-details/types'
import { graphApi } from '@/services/api/graph'
import { cn } from '@/utils/cn'

interface ExpandNeighborsPopoverProps {
  caseId: string
  nodeId: string
  nodeName: string
  /** Per-label counts of unloaded 1-hop neighbours of `nodeId`. */
  countsByLabel: Record<string, number>
  /** Called with the freshly-fetched neighbour subset that the parent should
   *  merge into its view. The parent's `mergeIntoView` is responsible for
   *  deduplication and persistence. */
  onMerge: (nodes: NeighborNode[], edges: NeighborEdge[]) => Promise<void> | void
  onClose: () => void
}

/**
 * Per-node expansion popover. Shows the breakdown of unloaded neighbours by
 * entity label and lets the user pull in just the labels they care about
 * (e.g. only `Location` from a SocialMediaPost). Fetches all 1-hop
 * neighbours in one round-trip, filters client-side per `+ Add` click —
 * cheaper than per-label API calls.
 */
export function ExpandNeighborsPopover({
  caseId,
  nodeId,
  nodeName,
  countsByLabel,
  onMerge,
  onClose,
}: ExpandNeighborsPopoverProps) {
  const { t } = useTranslation()
  const [busyLabel, setBusyLabel] = useState<string | 'all' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const labels = Object.entries(countsByLabel).sort((a, b) => b[1] - a[1])
  const total = labels.reduce((s, [, c]) => s + c, 0)

  async function expand(filterLabel: string | null) {
    setBusyLabel(filterLabel ?? 'all')
    setError(null)
    try {
      const res = await graphApi.getNodeNeighbors(caseId, nodeId)
      const nodes = (res.nodes ?? []) as unknown as NeighborNode[]
      const rels = (res.relationships ?? []) as unknown as NeighborEdge[]
      const filteredNodes = filterLabel
        ? nodes.filter(n => (n.labels ?? []).includes(filterLabel))
        : nodes
      const keepIds = new Set(filteredNodes.map(n => n.id))
      const filteredRels = rels.filter(r => keepIds.has(r.source) || keepIds.has(r.target))
      await onMerge(filteredNodes, filteredRels)
      onClose()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusyLabel(null)
    }
  }

  return (
    <div
      className={cn(
        'flex w-[280px] flex-col overflow-hidden rounded-xl max-md:max-w-[calc(100vw-1.5rem)]',
        'border border-border bg-popover text-popover-foreground shadow-xl'
      )}
    >
      <div className="flex items-start justify-between gap-2 border-b border-border/60 px-4 py-3">
        <div className="min-w-0">
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Expand neighbours
          </p>
          <p className="mt-0.5 truncate text-sm font-semibold text-foreground" title={nodeName}>
            {nodeName}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className={cn(
            '-mr-1 -mt-0.5 shrink-0 rounded-md p-1 text-muted-foreground max-md:p-2.5',
            'transition-colors hover:bg-accent/60 hover:text-foreground'
          )}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="max-h-[40vh] overflow-y-auto p-1.5">
        {labels.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-muted-foreground">
            No unloaded neighbours.
          </div>
        ) : (
          labels.map(([label, count]) => {
            const color = LABEL_COLORS[label] ?? LABEL_COLORS.Default
            const busy = busyLabel === label
            return (
              <button
                key={label}
                type="button"
                onClick={() => expand(label)}
                disabled={busyLabel !== null}
                className={cn(
                  'group flex w-full items-center justify-between gap-3 rounded-md px-2.5 py-2 text-left max-md:py-2.5',
                  'text-xs transition-colors hover:bg-accent/60 disabled:opacity-50'
                )}
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <span
                    className="size-2.5 shrink-0 rounded-full border"
                    style={{ backgroundColor: color.fill, borderColor: color.stroke }}
                  />
                  <span className="truncate font-medium text-foreground">{label}</span>
                </div>
                <div className="flex shrink-0 items-center gap-2 text-muted-foreground">
                  <span className="font-mono text-[11px] tabular-nums">{count}</span>
                  {busy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="h-3.5 w-3.5 opacity-50 group-hover:opacity-100" />
                  )}
                </div>
              </button>
            )
          })
        )}
      </div>
      {labels.length > 1 && (
        <div className="border-t border-border/60 p-2">
          <button
            type="button"
            onClick={() => expand(null)}
            disabled={busyLabel !== null}
            className={cn(
              'flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-1.5 max-md:py-2.5',
              'text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90',
              'disabled:opacity-50'
            )}
          >
            {busyLabel === 'all' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            Add all {total}
          </button>
        </div>
      )}
      {error && (
        <div className="border-t border-destructive/40 bg-destructive/10 px-3 py-2 text-[11px] text-destructive">
          {error}
        </div>
      )}
    </div>
  )
}
